import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { adminApi } from "@/lib/api";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { X, Save, Check } from "lucide-react";

/**
 * Permission modules and their actions.
 * Must match the backend PERMISSION_MODULES constant.
 */
const PERMISSION_MODULES: Record<string, string[]> = {
  users: ["list", "view", "edit", "delete", "restore", "resetPassword"],
  companies: ["list", "view", "edit", "delete", "restore"],
  reports: ["list", "view", "delete", "restore"],
  subscriptions: ["list", "view", "download_receipt"],
  audit_logs: ["list", "view"],
  settings: ["view", "edit"],
  pricing: ["view", "edit"],
  roles: ["list", "view", "create", "edit", "delete", "assign"],
  statistics: ["view"],
};

const MODULE_LABELS: Record<string, string> = {
  users: "Användare",
  companies: "Företag",
  reports: "Årsredovisningar",
  subscriptions: "Prenumerationer",
  audit_logs: "Systemloggar",
  settings: "Inställningar",
  pricing: "Prissättning",
  roles: "Roller",
  statistics: "Statistik",
};

const ACTION_LABELS: Record<string, string> = {
  list: "Lista",
  view: "Visa",
  edit: "Redigera",
  create: "Skapa",
  delete: "Ta bort",
  restore: "Återställ",
  assign: "Tilldela",
  resetPassword: "Återställ lösen",
  download_receipt: "Ladda ner kvitto",
};

interface AdminRoleModalProps {
  roleId: string | null; // null = create mode
  onClose: () => void;
  onSaved: () => void;
}

export function AdminRoleModal({ roleId, onClose, onSaved }: AdminRoleModalProps) {
  const isCreateMode = roleId === null;
  const [isLoading, setIsLoading] = useState(!isCreateMode);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const mouseDownTargetRef = useRef<EventTarget | null>(null);

  // Form state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [permissions, setPermissions] = useState<Record<string, string[]>>({});

  useEffect(() => {
    if (roleId) loadRole(roleId);
  }, [roleId]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const loadRole = async (id: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await adminApi.getRole(id);
      if (response.success && response.data) {
        const role = response.data;
        setName(role.name);
        setDescription(role.description || "");
        setPermissions(role.permissions);
      } else {
        setError(response.error?.message || "Kunde inte ladda roll");
      }
    } catch {
      setError("Kunde inte ansluta till servern");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError("Namn krävs");
      return;
    }

    // Filter out empty permission arrays
    const cleanPermissions: Record<string, string[]> = {};
    for (const [mod, actions] of Object.entries(permissions)) {
      if (actions.length > 0) {
        cleanPermissions[mod] = actions;
      }
    }

    setIsSaving(true);
    setError(null);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || undefined,
        permissions: cleanPermissions,
      };

      const response = isCreateMode
        ? await adminApi.createRole(payload as { name: string; description?: string; permissions: Record<string, string[]> })
        : await adminApi.updateRole(roleId!, payload);

      if (response.success) {
        onSaved();
        toast.success(isCreateMode ? "Rollen har skapats" : "Rollen har uppdaterats");
        onClose();
      } else {
        setError(response.error?.message || "Kunde inte spara roll");
      }
    } catch {
      toast.error("Kunde inte ansluta till servern");
    } finally {
      setIsSaving(false);
    }
  };

  const toggleAction = useCallback((module: string, action: string) => {
    setPermissions((prev) => {
      const current = prev[module] || [];
      const has = current.includes(action);
      const updated = has
        ? current.filter((a) => a !== action)
        : [...current, action];
      return { ...prev, [module]: updated };
    });
  }, []);

  const toggleAllForModule = useCallback((module: string) => {
    setPermissions((prev) => {
      const allActions = PERMISSION_MODULES[module];
      const current = prev[module] || [];
      const allSelected = allActions.every((a) => current.includes(a));
      return {
        ...prev,
        [module]: allSelected ? [] : [...allActions],
      };
    });
  }, []);

  const isAllSelected = (module: string) => {
    const allActions = PERMISSION_MODULES[module];
    const current = permissions[module] || [];
    return allActions.every((a) => current.includes(a));
  };

  const isSomeSelected = (module: string) => {
    const current = permissions[module] || [];
    return current.length > 0 && !isAllSelected(module);
  };

  const totalPermissionCount = Object.values(PERMISSION_MODULES).reduce((sum, actions) => sum + actions.length, 0);
  const selectedPermissionCount = Object.values(permissions).reduce((sum, actions) => sum + actions.length, 0);
  const allGlobalSelected = selectedPermissionCount === totalPermissionCount;

  const toggleAllGlobal = useCallback(() => {
    if (allGlobalSelected) {
      setPermissions({});
    } else {
      const all: Record<string, string[]> = {};
      for (const [mod, actions] of Object.entries(PERMISSION_MODULES)) {
        all[mod] = [...actions];
      }
      setPermissions(all);
    }
  }, [allGlobalSelected]);

  // Track mousedown to prevent closing when dragging from inside modal
  const handleMouseDown = (e: React.MouseEvent) => {
    mouseDownTargetRef.current = e.target;
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    if (
      e.target === overlayRef.current &&
      mouseDownTargetRef.current === overlayRef.current
    ) {
      onClose();
    }
    mouseDownTargetRef.current = null;
  };

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/20"
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {isCreateMode ? "Skapa ny roll" : "Redigera roll"}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-gray-100 transition-colors">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : (
            <div className="space-y-5">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Namn</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="T.ex. Support, Analytiker"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Beskrivning</label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Kort beskrivning av rollens behörigheter"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Permission matrix */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-gray-700">
                    Behörigheter
                    <span className="text-xs font-normal text-gray-400 ml-2">
                      {selectedPermissionCount} av {totalPermissionCount}
                    </span>
                  </label>
                  <button
                    type="button"
                    onClick={toggleAllGlobal}
                    className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                  >
                    {allGlobalSelected ? "Avmarkera alla" : "Markera alla"}
                  </button>
                </div>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  {Object.entries(PERMISSION_MODULES).map(([module, actions], idx) => (
                    <div
                      key={module}
                      className={idx > 0 ? "border-t border-gray-200" : ""}
                    >
                      {/* Module header row */}
                      <div className="flex items-center gap-3 px-4 py-2.5 bg-gray-50">
                        <button
                          type="button"
                          onClick={() => toggleAllForModule(module)}
                          className={`flex items-center justify-center w-4 h-4 rounded border transition-colors ${
                            isAllSelected(module)
                              ? "bg-primary-600 border-primary-600"
                              : isSomeSelected(module)
                                ? "bg-primary-200 border-primary-400"
                                : "border-gray-300 bg-white"
                          }`}
                        >
                          {(isAllSelected(module) || isSomeSelected(module)) && (
                            <Check size={12} className="text-white" />
                          )}
                        </button>
                        <span className="text-sm font-medium text-gray-700">
                          {MODULE_LABELS[module] || module}
                        </span>
                      </div>

                      {/* Actions row */}
                      <div className="flex flex-wrap gap-2 px-4 py-2.5 pl-11">
                        {actions.map((action) => {
                          const isChecked = (permissions[module] || []).includes(action);
                          return (
                            <label
                              key={action}
                              className="inline-flex items-center gap-1.5 cursor-pointer select-none"
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => toggleAction(module, action)}
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                              />
                              <span className="text-xs text-gray-600">
                                {ACTION_LABELS[action] || action}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Error message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {!isLoading && (
          <div className="flex items-center justify-end gap-3 p-5 border-t border-gray-200">
            <Button variant="outline" onClick={onClose}>
              Avbryt
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? <Spinner size="sm" /> : <Save size={16} />}
              {isCreateMode ? "Skapa" : "Spara"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
